import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark, faChevronRight, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import Seo from '../../components/Seo/Seo';
import HelpHeader from './HelpHeader';
import PublicBreadcrumb from '../../components/PublicBreadcrumb/PublicBreadcrumb';
import { resolveHelpIcon, helpTint, helpTintBg } from './helpDisplay';
import MissionSection from '../../components/Home/MissionSection';
import Footer from '../../components/Home/Footer';
import GDPRConsent from '../../components/Home/GDPRConsent';
import { API_BASE_URL } from '../../api';
import '../Help.scss';

const HelpHome = () => {
	const { i18n } = useTranslation();
	const [query, setQuery] = useState('');
	const [categories, setCategories] = useState([]);
	const [articles, setArticles] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		Promise.all([
			axios.get(`${API_BASE_URL}/api/v1/help/categories`),
			axios.get(`${API_BASE_URL}/api/v1/help/articles`),
		])
			.then(([categoriesRes, articlesRes]) => {
				setCategories(categoriesRes.data);
				setArticles(articlesRes.data);
			})
			.catch(() => { setCategories([]); setArticles([]); })
			.finally(() => setLoading(false));
	}, []);

	const categoriesById = useMemo(() => {
		const map = {};
		categories.forEach(c => { map[c.id] = c; });
		return map;
	}, [categories]);

	const results = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return [];
		return articles.filter(a =>
			a.title.toLowerCase().includes(q) ||
			(a.excerpt || '').toLowerCase().includes(q) ||
			(a.content || '').toLowerCase().includes(q)
		);
	}, [query, articles]);

	return (
		<div className="help-page">
			<Seo
				title="Centre d'aide — Saydalani"
				description="Guides, réponses aux questions fréquentes et bonnes pratiques pour tirer le meilleur de Saydalani."
				path={`/${i18n.language}/help`}
			/>

			<HelpHeader />
			<PublicBreadcrumb className="pub-crumbs--bar" maxWidth={1360} gutter="24px" items={[{ label: "Centre d'aide" }]} />

			<div className="help-hero">
				<h1>Comment pouvons-nous vous aider ?</h1>
				<p>Guides, réponses aux questions fréquentes et bonnes pratiques pour tirer le meilleur de Saydalani.</p>

				<div className="help-search">
					<FontAwesomeIcon icon={faMagnifyingGlass} className="help-search__icon" />
					<input
						type="text"
						className="help-search__input"
						placeholder="Rechercher un article (ex: inventaire, facture, stock...)"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
					{query && (
						<button type="button" className="help-search__clear" onClick={() => setQuery('')}>
							<FontAwesomeIcon icon={faXmark} />
						</button>
					)}
				</div>
			</div>

			<div className="help-container">
				{loading ? (
					<div className="help-empty">
						<span className="spinner-border spinner-border-sm" />
						<p>Chargement...</p>
					</div>
				) : query.trim() ? (
					<div className="help-results">
						<p className="help-results__count">
							{results.length} résultat{results.length > 1 ? 's' : ''} pour "{query}"
						</p>
						{results.length === 0 ? (
							<div className="help-empty">
								<FontAwesomeIcon icon={faCircleQuestion} />
								<p>Aucun article ne correspond à votre recherche.</p>
							</div>
						) : (
							<div className="help-post-list">
								{results.map(post => {
									const cat = categoriesById[post.categoryId];
									return (
										<NavLink key={post.id} to={`/${i18n.language}/help/${cat?.slug}/${post.slug}`} className="help-post-list__item">
											<span className="help-post-list__icon" style={{ background: helpTintBg(cat?.color), color: helpTint(cat?.color) }}>
												<FontAwesomeIcon icon={resolveHelpIcon(cat?.icon)} />
											</span>
											<span className="help-post-list__body">
												<span className="help-post-list__title">{post.title}</span>
												<span className="help-post-list__excerpt">{post.excerpt}</span>
											</span>
											<FontAwesomeIcon icon={faChevronRight} className="help-post-list__arrow" />
										</NavLink>
									);
								})}
							</div>
						)}
					</div>
				) : categories.length === 0 ? (
					<div className="help-empty">
						<FontAwesomeIcon icon={faCircleQuestion} />
						<p>Le centre d'aide sera bientôt disponible.</p>
					</div>
				) : (
					<div className="help-category-grid">
						{categories.map(cat => (
							<NavLink key={cat.id} to={`/${i18n.language}/help/${cat.slug}`} className="help-category-card">
								<span className="help-category-card__icon" style={{ background: helpTintBg(cat.color), color: helpTint(cat.color) }}>
									<FontAwesomeIcon icon={resolveHelpIcon(cat.icon)} />
								</span>
								<span className="help-category-card__label">{cat.label}</span>
								<span className="help-category-card__desc">{cat.description}</span>
								<span className="help-category-card__count">{cat.articleCount} article{cat.articleCount > 1 ? 's' : ''}</span>
							</NavLink>
						))}
					</div>
				)}
			</div>

			<MissionSection />
			<Footer />
			<GDPRConsent />
		</div>
	);
};

export default HelpHome;
